import type { Card } from '@/entities/board';

export type TaskGroup = 'overdue' | 'today' | 'upcoming' | 'none' | 'completed';

export const TASK_GROUPS: readonly TaskGroup[] = [
  'overdue',
  'today',
  'upcoming',
  'none',
  'completed',
];

/**
 * The planner group of a task, from the server's own deadline state (never recomputed from the
 * browser clock, so every surface agrees with the Kanban badge): completed tasks form their own
 * group; the rest are overdue / due today / upcoming, or have no deadline.
 */
export function groupOf(card: Pick<Card, 'deadline' | 'completed_at' | 'is_overdue'>): TaskGroup {
  if (card.completed_at !== null) return 'completed';
  if (card.deadline === null) return 'none';
  if (card.is_overdue || card.deadline.state === 'overdue') return 'overdue';
  if (card.deadline.state === 'today') return 'today';
  return 'upcoming';
}

/** Tasks per group, each group keeping the server's order (soonest first). */
export function groupTasks<T extends Pick<Card, 'deadline' | 'completed_at' | 'is_overdue'>>(
  cards: readonly T[],
): Record<TaskGroup, T[]> {
  const groups: Record<TaskGroup, T[]> = {
    overdue: [],
    today: [],
    upcoming: [],
    none: [],
    completed: [],
  };
  for (const card of cards) groups[groupOf(card)].push(card);
  return groups;
}

/** Where a task opens: its own card inside its original board (the existing deep link). */
export function taskPath(boardId: string, cardId: string): string {
  return `/app/boards/${boardId}/cards/${cardId}`;
}
